import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { 
  Typography, 
  Box, 
  Paper, 
  Button, 
  CircularProgress, 
  Alert,
  TextField,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText
} from '@mui/material';
import { 
  ArrowBack as ArrowBackIcon, 
  Save as SaveIcon, 
  Cancel as CancelIcon 
} from '@mui/icons-material';
import userService from '../services/userService';

const UserFormScreen = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditMode = Boolean(id);
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
    role: 'viewer'
  });
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    // Si estamos en modo edición, cargar datos del usuario
    if (isEditMode) {
      setLoading(true);
      
      // Cargar datos reales del usuario desde la API
      userService.getUserById(id)
        .then(userData => {
          setFormData({
            ...userData,
            password: '',
            confirmPassword: ''
          });
        })
        .catch(err => {
          setError(`Error al cargar datos del usuario: ${err.message || 'Error desconocido'}`);
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, [isEditMode, id]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    // Validación básica
    if (!formData.name || !formData.email || !formData.role) {
      setError('Por favor completa todos los campos obligatorios');
      return;
    }
    
    // Validación de contraseña para nuevo usuario
    if (!isEditMode && (!formData.password || formData.password.length < 6)) {
      setError('La contraseña debe tener al menos 6 caracteres');
      return;
    }
    
    // Validación de coincidencia de contraseñas
    if (formData.password && formData.password !== formData.confirmPassword) {
      setError('Las contraseñas no coinciden');
      return;
    }
    
    try {
      setSubmitting(true);
      
      // Lógica para guardar o actualizar el usuario
      if (isEditMode) {
        // Preparar datos para actualización
        const updateData = {
          name: formData.name,
          email: formData.email,
          role: formData.role
        };
        
        // Solo incluir contraseña si se proporcionó una nueva
        if (formData.password) {
          updateData.password = formData.password;
        }
        
        await userService.updateUser(id, updateData);
      } else {
        // Crear nuevo usuario
        await userService.createUser({
          name: formData.name,
          email: formData.email,
          password: formData.password,
          role: formData.role
        });
      }
      
      navigate('/admin/users');
    } catch (err) {
      setError('Error al guardar el usuario: ' + (err.message || 'Error desconocido'));
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Button 
          component={Link} 
          to="/admin/users" 
          startIcon={<ArrowBackIcon />}
        >
          Volver a Usuarios
        </Button>
      </Box>

      <Typography variant="h4" component="h1" sx={{ mb: 3 }}>
        {isEditMode ? 'Editar Usuario' : 'Nuevo Usuario'}
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <Paper component="form" onSubmit={handleSubmit} sx={{ p: 3 }}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <TextField
              required
              fullWidth
              label="Nombre Completo"
              name="name"
              value={formData.name}
              onChange={handleChange}
            />
          </Grid>
          
          <Grid item xs={12}>
            <TextField
              required
              fullWidth
              label="Correo Electrónico"
              name="email"
              type="email"
              value={formData.email}
              onChange={handleChange}
            />
          </Grid>
          
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label={isEditMode ? 'Nueva Contraseña (dejar en blanco para mantener)' : 'Contraseña'}
              name="password"
              type="password"
              value={formData.password}
              onChange={handleChange}
              required={!isEditMode}
            />
          </Grid>
          
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Confirmar Contraseña"
              name="confirmPassword"
              type="password"
              value={formData.confirmPassword}
              onChange={handleChange}
              required={!isEditMode || formData.password.length > 0}
            />
          </Grid>
          
          <Grid item xs={12} md={6}>
            <FormControl fullWidth required>
              <InputLabel id="role-label">Rol</InputLabel>
              <Select
                labelId="role-label"
                name="role"
                value={formData.role}
                onChange={handleChange}
                label="Rol"
              >
                <MenuItem value="admin">Administrador</MenuItem>
                <MenuItem value="editor">Editor</MenuItem>
                <MenuItem value="viewer">Visualizador</MenuItem>
              </Select>
              <FormHelperText>
                El rol determina los permisos del usuario en el sistema
              </FormHelperText>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 2 }}>
            <Button 
              component={Link} 
              to="/admin/users" 
              variant="outlined" 
              startIcon={<CancelIcon />}
            >
              Cancelar
            </Button>
            <Button 
              type="submit"
              variant="contained" 
              startIcon={<SaveIcon />}
              disabled={submitting}
            >
              {submitting ? <CircularProgress size={24} /> : 'Guardar'}
            </Button>
          </Grid>
        </Grid>
      </Paper>
    </Box>
  );
};

export default UserFormScreen; 